<template>
  <CModal
    :show.sync="show"
    :title="modalTitle"
    centered
    @update:show="onModalClose"
    @dragover.native.prevent.stop
    @drop.native.prevent.stop
  >
    <!-- STEP 1: Upload -->
    <div v-if="currentStep === 'upload'" class="upload-step">
      <div
        class="drop-zone"
        :class="{'drop-zone-active': isDragging}"
        @drop.prevent.stop="onFileDrop"
        @dragover.prevent.stop="isDragging = true"
        @dragleave.stop="isDragging = false"
        @click="$refs.fileInput.click()"
      >
        <CIcon name="cipUploadCloud" size="4xl" class="mb-3 text-primary"/>
        <p class="mb-2">
          <translate translate-context="photosearch.identify">
            Drop an image here or click to browse
          </translate>
        </p>
        <small class="text-muted">
          <translate translate-context="photosearch.identify">
            JPEG only, max 10MB
          </translate>
        </small>
      </div>
      <input
        ref="fileInput"
        type="file"
        accept="image/jpeg,image/jpg"
        style="display: none"
        @change="onFileSelect"
      />
    </div>

    <!-- STEP 2: Multiple Faces Selection -->
    <div v-if="currentStep === 'select-face'" class="select-face-step">
      <p class="text-center mb-3">
        <translate translate-context="photosearch.identify">
          Multiple faces detected. Select the person to identify:
        </translate>
      </p>
      <div class="image-with-overlays">
        <img :src="uploadedImagePreview" class="uploaded-image"
             :alt="$pgettext('photosearch.identify', 'Uploaded image')"/>
        <FaceOverlay
          v-for="(face, index) in detectedFaces"
          :key="index"
          :face="face"
          :index="index"
          @select="onFaceSelect"
        />
      </div>
    </div>

    <!-- STEP 3: Identifying -->
    <div v-if="currentStep === 'searching'" class="searching-step text-center py-5">
      <CSpinner color="primary" style="width: 3rem; height: 3rem;" class="mb-3"/>
      <p>
        <translate translate-context="photosearch.identify">
          Identifying person...
        </translate>
      </p>
    </div>

    <!-- STEP 4: Results -->
    <div v-if="currentStep === 'results'" class="results-step">
      <p class="text-center mb-3">
        <translate translate-context="photosearch.identify" :translate-params="{count: faceSearchResults.length}">
          Found %{count} possible matches. Select the correct person:
        </translate>
      </p>
      <CRow>
        <CCol
          v-for="cluster in faceSearchResults"
          :key="cluster.id"
          cols="12"
          md="6"
          lg="4"
          class="mb-3"
        >
          <ClusterResultCard
            :cluster="cluster"
            @select="onClusterSelect"
          />
        </CCol>
      </CRow>
    </div>

    <!-- STEP 5: No Results -->
    <div v-if="currentStep === 'no-results'" class="no-results-step text-center py-4">
      <CIcon name="cipSearchX" size="4xl" class="mb-3 text-muted"/>
      <h5>
        <translate translate-context="photosearch.identify">
          Person not found
        </translate>
      </h5>
      <p class="text-muted">
        <translate translate-context="photosearch.identify">
          This person doesn't appear in any photos. Try a different image.
        </translate>
      </p>
    </div>

    <!-- Error Display -->
    <CAlert v-if="faceSearchError" color="danger" class="mt-3">
      {{ faceSearchError }}
    </CAlert>

    <!-- Footer -->
    <template #footer>
      <CButton
        v-if="showBackButton"
        color="secondary"
        @click="goBack"
      >
        <translate translate-context="photosearch.identify">Back</translate>
      </CButton>
      <CButton
        color="secondary"
        @click="close"
      >
        <translate translate-context="photosearch.identify">Close</translate>
      </CButton>
    </template>
  </CModal>
</template>

<script>
import {mapActions, mapGetters} from "vuex";
import FaceOverlay from "./FaceOverlay.vue";
import ClusterResultCard from "./ClusterResultCard.vue";
import {validateImageFile, compressImageForSearch} from "../utils/imageCropper";

export default {
  name: "FaceSearchModal",
  emits: ['filter'],
  components: {
    FaceOverlay,
    ClusterResultCard
  },
  data() {
    return {
      show: false,
      isDragging: false
    }
  },
  computed: {
    ...mapGetters('photoSearch', [
      'isFaceSearching',
      'faceSearchError',
      'faceSearchResults',
      'detectedFaces',
      'uploadedImagePreview',
      'hasFaceSearchResults',
      'hasDetectedFaces',
      'filters'
    ]),

    currentStep() {
      if (this.isFaceSearching) return 'searching'
      if (this.hasDetectedFaces) return 'select-face'
      // Only show results/no-results if we have an uploaded image (search was performed)
      if (this.uploadedImagePreview && this.faceSearchResults) {
        if (this.faceSearchResults.length > 1) return 'results'
        if (this.faceSearchResults.length === 0) return 'no-results'
      }
      return 'upload'
    },

    modalTitle() {
      const steps = {
        'upload': this.$pgettext('photosearch.identify', 'Search by face'),
        'select-face': this.$pgettext('photosearch.identify', 'Select Face'),
        'searching': this.$pgettext('photosearch.identify', 'Identifying...'),
        'results': this.$pgettext('photosearch.identify', 'Select Person'),
        'no-results': this.$pgettext('photosearch.identify', 'Not Found')
      }
      return steps[this.currentStep] || this.$pgettext('photosearch.identify', 'Search by face')
    },

    showBackButton() {
      return ['select-face', 'results', 'no-results'].includes(this.currentStep)
    }
  },
  methods: {
    ...mapActions('photoSearch', [
      'identifyPersonInImage',
      'identifyPersonFromSelection',
      'applyPersonFilter',
      'clearPersonIdentification'
    ]),

    open() {
      this.show = true
      this.isDragging = false
      this.clearPersonIdentification()
    },

    close() {
      this.show = false
    },

    onModalClose(isOpen) {
      if (!isOpen) {
        // Clear state when modal closes
        this.clearPersonIdentification()
      }
    },

    goBack() {
      // Reset to upload step
      this.clearPersonIdentification()
    },

    async onFileSelect(event) {
      const file = event.target.files[0]
      if (file) {
        await this.handleImageFile(file)
      }
      // Reset input
      event.target.value = ''
    },

    async onFileDrop(event) {
      this.isDragging = false
      const file = event.dataTransfer.files[0]
      if (file) {
        await this.handleImageFile(file)
      }
    },

    async handleImageFile(file) {
      // Validate file
      const validation = validateImageFile(file)
      if (!validation.valid) {
        this.notifyError(validation.error)
        return
      }

      try {
        // Compress image to max 1MB for faster upload
        const compressedFile = await compressImageForSearch(file)

        // Identify person
        const result = await this.identifyPersonInImage(compressedFile)

        // Handle results
        if (result.autoApplied) {
          // Single person identified and filter auto-applied
          this.emitFilterForCluster(result.cluster)
          this.notifySuccess(this.$pgettext('photosearch.identify', 'Person identified - showing all their photos'))
          this.close()
        } else if (result.multipleFaces) {
          // Multiple faces detected - UI will show selection
          // Do nothing, currentStep will automatically switch to 'select-face'
        } else if (result.clusters && result.clusters.length === 0) {
          // No results found
          // UI will show 'no-results' step
        }
        // If multiple possible persons, UI will show results step automatically
      } catch (error) {
        // Error already handled by action and stored in state
        console.error('Person identification error:', error)
      }
    },

    async onFaceSelect(faceIndex) {
      try {
        // Identify person using selected bbox
        const result = await this.identifyPersonFromSelection({faceIndex})

        // Handle results (same logic as handleImageFile)
        if (result.autoApplied) {
          // Single person identified and filter auto-applied
          this.emitFilterForCluster(result.cluster)
          this.notifySuccess(this.$pgettext('photosearch.identify', 'Person identified - showing all their photos'))
          this.close()
        } else if (result.clusters && result.clusters.length === 0) {
          // No results found - UI will show 'no-results' step
        }
        // If multiple possible persons, UI will show results step automatically
      } catch (error) {
        this.notifyError(this.$pgettext('photosearch.identify', 'Failed to process selected face'))
        console.error('Face selection error:', error)
      }
    },

    emitFilterForCluster(cluster) {
      const newFilters = {
        ...this.filters,
        cluster_id: cluster.id
      }
      this.$emit('filter', newFilters)
    },

    async onClusterSelect(cluster) {
      try {
        await this.applyPersonFilter(cluster)
        this.emitFilterForCluster(cluster)
        this.notifySuccess(this.$pgettext('photosearch.identify', 'Showing all photos of this person'))
        this.close()
      } catch (error) {
        this.notifyError(this.$pgettext('photosearch.identify', 'Failed to apply filter'))
      }
    }
  }
}
</script>

<style scoped>
.drop-zone {
  border: 2px dashed #ccc;
  border-radius: 8px;
  padding: 60px 20px;
  text-align: center;
  cursor: pointer;
  transition: all 0.3s ease;
  background: #fafafa;
}

.drop-zone:hover {
  border-color: var(--primary);
  background: #f0f8ff;
}

.drop-zone-active {
  border-color: var(--primary);
  background: #e8f4fd;
  transform: scale(1.02);
}

.image-with-overlays {
  position: relative;
  width: 100%;
  max-width: 800px;
  margin: 0 auto;
}

.uploaded-image {
  width: 100%;
  height: auto;
  display: block;
  border-radius: 8px;
}

.searching-step {
  min-height: 200px;
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
}

.results-step {
  max-height: 500px;
  overflow-y: auto;
}

.no-results-step {
  min-height: 200px;
}
</style>
